import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProtectedRoute, AdminRoute, adminPanelRoles } from "./components/ProtectedRoute";
import AppErrorBoundary from "./components/AppErrorBoundary";
import AgreementModal from "./components/AgreementModal";
import SocialAccessBoundary from "./components/SocialAccessBoundary";
import { Toaster } from "./components/ui/sonner";
import { publicAPI } from "./lib/api";
import { buildPublicSiteUrl } from "./lib/backendUrl";
import { isWhiteLabelArtistPortalUser } from "./lib/entitlements";
import { getCurrentHostname, isPortalSubdomain, shouldLookupWhiteLabelPortalByHost } from "./lib/portalHost";

// Public Pages
const Home = lazy(() => import("./pages/Home"));
const Pricing = lazy(() => import("./pages/Pricing"));
const TRBSelect = lazy(() => import("./pages/TRBSelect"));
const TRBMusicGroup = lazy(() => import("./pages/TRBMusicGroupHome"));
const TRBMusicGroupSubmission = lazy(() => import("./pages/TRBMusicGroup"));
const TRBMusicGroupDashboard = lazy(() => import("./pages/TRBMusicGroupDashboard"));
const TRBMusicGroupHowItWorks = lazy(() => import("./pages/TRBMusicGroupHowItWorks"));
const Giveaway = lazy(() => import("./pages/Giveaway"));
const MusicNoteBadge = lazy(() => import("./pages/MusicNoteBadge"));
const Compare = lazy(() => import("./pages/Compare"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const SignupVerification = lazy(() => import("./pages/SignupVerification"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const AccountBanStatus = lazy(() => import("./pages/AccountBanStatus"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const InvitePage = lazy(() => import("./pages/Invite"));
const TakedownPage = lazy(() => import("./pages/Takedown"));
const PreSavePage = lazy(() => import("./pages/PreSave"));
const PublicArtistProfilePage = lazy(() => import("./pages/PublicArtistProfile"));
const PublicAlbumPage = lazy(() => import("./pages/PublicAlbumPage"));
const SocialFeed = lazy(() => import("./pages/SocialFeed"));
const SocialSignup = lazy(() => import("./pages/SocialSignup"));
const SocialPost = lazy(() => import("./pages/SocialPost"));
const SocialUserProfile = lazy(() => import("./pages/SocialUserProfile"));
const SocialVerification = lazy(() => import("./pages/SocialVerification"));
const AwardDisplay = lazy(() => import("./pages/AwardDisplay"));
const AwardWall = lazy(() => import("./pages/AwardWall"));
const Careers = lazy(() => import("./pages/Careers"));
const News = lazy(() => import("./pages/News"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const PublicSlugLanding = lazy(() => import("./pages/PublicSlugLanding"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CopyrightPolicy = lazy(() => import("./pages/CopyrightPolicy"));
const DMCA = lazy(() => import("./pages/DMCA"));
const DmcaNotice = lazy(() => import("./pages/DmcaNotice"));
const CommunityGuidelines = lazy(() => import("./pages/CommunityGuidelines"));
const VerificationPolicy = lazy(() => import("./pages/VerificationPolicy"));
const SponsoredContentPolicy = lazy(() => import("./pages/SponsoredContentPolicy"));
const TrustSafety = lazy(() => import("./pages/TrustSafety"));
import Maintenance from "./pages/Maintenance";
const ProductSuite = lazy(() => import("./pages/ProductSuite"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Stores = lazy(() => import("./pages/Stores"));
const PublicStore = lazy(() => import("./pages/PublicStore"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const Developer = lazy(() => import("./pages/Developer"));
const Mfeb = lazy(() => import("./pages/Mafa"));
const CMGSite = lazy(() => import("./pages/CMGSite"));
const CMGAdmin = lazy(() => import("./pages/CMGAdmin"));
const OurDesigns = lazy(() => import("./pages/OurDesigns"));
const PublicDemo = lazy(() => import("./pages/PublicDemo"));
const Freelance = lazy(() => import("./pages/Freelance"));
const JaxonWestMusicAdmin = lazy(() => import("./pages/JaxonWestMusicAdmin"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const BookDemo = lazy(() => import("./pages/BookDemo"));
const BookCalendar = lazy(() => import("./pages/BookCalendar"));
const HowDistributionWorks = lazy(() => import("./pages/HowDistributionWorks"));
const ContentIDInfo = lazy(() => import("./pages/ContentIDInfo"));
const WhySwitch = lazy(() => import("./pages/WhySwitch"));
const DistroKidAlternative = lazy(() => import("./pages/DistroKidAlternative"));
const TuneCoreAlternative = lazy(() => import("./pages/TuneCoreAlternative"));
const MusicDistributionForLabels = lazy(() => import("./pages/MusicDistributionForLabels"));
const ReleaseCommandCenterFeature = lazy(() => import("./pages/features/ReleaseCommandCenterFeature"));
const SmartLinksFeature = lazy(() => import("./pages/features/SmartLinksFeature"));
const TeamWorkflowFeature = lazy(() => import("./pages/features/TeamWorkflowFeature"));
const FanCrmFeature = lazy(() => import("./pages/features/FanCrmFeature"));
const LabelBrandingFeature = lazy(() => import("./pages/features/LabelBrandingFeature"));
const BulkUploadFeature = lazy(() => import("./pages/features/BulkUploadFeature"));
const SupportStatusFeature = lazy(() => import("./pages/features/SupportStatusFeature"));
const B2BApp = lazy(() => import("./b2b/App"));
const DistroGridApp = lazy(() => import("./distrogrid/DistroGridApp"));
const SafeBasePanel = lazy(() => import("./alarm/SafeBasePanel"));

// Onboarding
const OnboardingPlan = lazy(() => import("./pages/onboarding/Plan"));
const OnboardingProfile = lazy(() => import("./pages/onboarding/Profile"));
const OnboardingEnterprise = lazy(() => import("./pages/onboarding/Enterprise"));

// Portal Pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ApiPortal = lazy(() => import("./pages/ApiPortal"));
const Upload = lazy(() => import("./pages/Upload"));
const CatalogMigration = lazy(() => import("./pages/CatalogMigration"));
const Music = lazy(() => import("./pages/Music"));
const Mastering = lazy(() => import("./pages/Mastering"));
const Bank = lazy(() => import("./pages/Bank"));
const Billing = lazy(() => import("./pages/Billing"));
const AcrCloudScanner = lazy(() => import("./pages/AcrCloudScanner"));
const ContentIDDashboard = lazy(() => import("./pages/ContentIDDashboard"));
const LabelTeam = lazy(() => import("./pages/LabelTeam"));
const EnterpriseArtists = lazy(() => import("./pages/EnterpriseArtists"));
const Support = lazy(() => import("./pages/Support"));
const SupportArticle = lazy(() => import("./pages/SupportArticle"));
const SupportNewTicket = lazy(() => import("./pages/SupportNewTicket"));
const SupportTicket = lazy(() => import("./pages/SupportTicket"));
const Status = lazy(() => import("./pages/Status"));
const VerificationPortal = lazy(() => import("./pages/VerificationPortal"));
const Account = lazy(() => import("./pages/Account"));
const AccountStanding = lazy(() => import("./pages/AccountStanding"));
const SecurityCenter = lazy(() => import("./pages/SecurityCenter"));
const PrivacyCenter = lazy(() => import("./pages/PrivacyCenter"));
const Awards = lazy(() => import("./pages/Awards"));
const Fans = lazy(() => import("./pages/Fans"));
const InstantClaim = lazy(() => import("./pages/InstantClaim"));
const TRBVideoComingSoon = lazy(() => import("./pages/TRBVideoComingSoon"));
const ArtistProfileSettings = lazy(() => import("./pages/ArtistProfileSettings"));
const BulkUpload = lazy(() => import("./pages/BulkUpload"));
const ReleaseCommandCenter = lazy(() => import("./pages/ReleaseCommandCenter"));
const EnterpriseDashboard = lazy(() => import("./pages/EnterpriseDashboard"));
const EnterpriseAccessDenied = lazy(() => import("./pages/EnterpriseAccessDenied"));
const LabelWebsite = lazy(() => import("./pages/LabelWebsite"));
const ArtistWebsite = lazy(() => import("./pages/ArtistWebsite"));
const BioLinks = lazy(() => import("./pages/BioLinks"));
const PreSaveCampaigns = lazy(() => import("./pages/PreSaveCampaigns"));
const PublicBioLink = lazy(() => import("./pages/PublicBioLink"));
const ArtistSolutions = lazy(() => import("./pages/solutions/Artist"));
const LabelSolutions = lazy(() => import("./pages/solutions/Label"));
const ManagerSolutions = lazy(() => import("./pages/solutions/Manager"));
const DummyStoreDashboard = lazy(() => import("./pages/DummyStoreDashboard"));
const TRBStoreDashboard = lazy(() => import("./pages/TRBStoreDashboard"));

// Admin Pages
const AdminQueue = lazy(() => import("./pages/admin/Queue"));
const AdminRiskQueue = lazy(() => import("./pages/admin/RiskQueue"));
const AdminReleases = lazy(() => import("./pages/admin/Releases"));
const AdminBank = lazy(() => import("./pages/admin/Bank"));
const AdminBilling = lazy(() => import("./pages/admin/Billing"));
const AdminTakedowns = lazy(() => import("./pages/admin/Takedowns"));
const AdminPartners = lazy(() => import("./pages/admin/Partners"));
const AdminDeveloper = lazy(() => import("./pages/admin/Developer"));
const AdminTickets = lazy(() => import("./pages/admin/Tickets"));
const AdminReceiving = lazy(() => import("./pages/admin/Receiving"));
const AdminMailboxSettings = lazy(() => import("./pages/admin/MailboxSettings"));
const AdminHelpArticles = lazy(() => import("./pages/admin/HelpArticles"));
const AdminExport = lazy(() => import("./pages/admin/Export"));
const AdminDDEX = lazy(() => import("./pages/admin/DDEX"));
const AdminDDEXTestCreator = lazy(() => import("./pages/admin/DDEXTestCreator"));
const AdminSpecialAccess = lazy(() => import("./pages/admin/SpecialAccess"));
const AdminStaff = lazy(() => import("./pages/admin/Staff"));
const RocketChatStaff = lazy(() => import("./pages/admin/RocketChatStaff"));
const AdminSiteStaff = lazy(() => import("./pages/admin/SiteStaff"));
const AdminAccountManagers = lazy(() => import("./pages/admin/AccountManagers"));
const AdminModeration = lazy(() => import("./pages/admin/Moderation"));
const AdminDistroGrid = lazy(() => import("./pages/admin/DistroGrid"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminLyrics = lazy(() => import("./pages/admin/Lyrics"));
const AdminCareers = lazy(() => import("./pages/admin/Careers"));
const AdminMarketing = lazy(() => import("./pages/admin/Marketing"));
const AdminCalendar = lazy(() => import("./pages/admin/Calendar"));
const AdminOurDesigns = lazy(() => import("./pages/admin/OurDesignsAdmin"));
const AdminMfeb = lazy(() => import("./pages/admin/Mafa"));
const AdminAffiliates = lazy(() => import("./pages/admin/Affiliates"));
const AdminNews = lazy(() => import("./pages/admin/News"));
const AdminAddedUpdates = lazy(() => import("./pages/admin/AddedUpdates"));
const AdminStatusComponents = lazy(() => import("./pages/admin/StatusComponents"));
const AdminRoadmap = lazy(() => import("./pages/admin/Roadmap"));
const AdminLegal = lazy(() => import("./pages/admin/Legal"));
const AdminCompanyPolicies = lazy(() => import("./pages/admin/CompanyPolicies"));
const AdminCompliance = lazy(() => import("./pages/admin/Compliance"));
const AdminBoardroom = lazy(() => import("./pages/admin/Boardroom"));
const AdminBoardroomDocuments = lazy(() => import("./pages/admin/BoardroomDocuments"));
const AdminFFM = lazy(() => import("./pages/admin/FFM"));
const AdminPresaves = lazy(() => import("./pages/admin/Presaves"));
const AdminOwnerVault = lazy(() => import("./pages/admin/OwnerVault"));
const AdminEnterpriseAccess = lazy(() => import("./pages/admin/EnterpriseAccess"));
const AdminStaffMenu = lazy(() => import("./pages/admin/StaffMenu"));
const AdminStaffHelper = lazy(() => import("./pages/admin/StaffHelper"));
const AdminStaffHelp = lazy(() => import("./pages/admin/StaffHelp"));
const AdminContentID = lazy(() => import("./pages/admin/ContentID"));
const AdminDemoAccounts = lazy(() => import("./pages/admin/DemoAccounts"));
const AdminAcrCloudScanner = lazy(() => import("./pages/admin/AcrCloudScanner"));
const AdminNeedsAttention = lazy(() => import("./pages/admin/NeedsAttention"));
const AdminArtistAwards = lazy(() => import("./pages/admin/ArtistAwards"));
const AdminAwardWall = lazy(() => import("./pages/admin/AwardWall"));
const AdminSocial = lazy(() => import("./pages/admin/Social"));

const NOINDEX_EXACT_PATHS = new Set([
  '/login',
  '/signup',
  '/signup/verify',
  '/forgot-password',
  '/reset-password',
  '/change-password',
  '/account-ban-status',
  '/accountbanstatus',
  '/catalog-migration',
  '/mastering',
  '/security',
  '/privacy-center',
  '/accept-invite',
  '/dashboard',
  '/artist-profile',
  '/api-portal',
  '/instant-claim',
  '/bulk-upload',
  '/music',
  '/videos',
  '/bank',
  '/billing',
  '/account',
  '/account-standing',
  '/awards',
  '/tools',
  '/tools/acrcloud-scanner',
  '/content-id',
  '/fans',
  '/team',
  '/overview',
  '/catalog',
  '/video-network',
  '/vevo',
  '/finance',
  '/oac-request',
  '/website',
  '/artist-site',
  '/cmgadmin',
  '/alarm',
  '/ourdesignsadmin',
  '/dummy-store',
  '/trb-music-group/dashboard',
]);

const NOINDEX_PATH_PREFIXES = [
  '/admin',
  '/invite/',
  '/payment/',
  '/onboarding/',
  '/upload',
  '/enterprise/',
  '/videos/channel/create',
  '/support/new',
  '/support/tickets/',
  '/verification/',
  '/music/',
  '/catalog-migration/',
  '/alarm/',
  '/cmgadmin/',
];

function upsertRobotsMeta(content) {
  let element = document.querySelector('meta[name="robots"]');
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', 'robots');
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  return null;
}

function RouteSeoPolicy() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname || '/';
    const shouldNoindex =
      NOINDEX_EXACT_PATHS.has(path) ||
      NOINDEX_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix));

    upsertRobotsMeta(shouldNoindex ? 'noindex, nofollow' : 'index, follow');
  }, [location.pathname]);

  return null;
}

function HomeRoute() {
  const { user, loading: authLoading } = useAuth();
  const hostname = getCurrentHostname();
  const isPortalHost = isPortalSubdomain(hostname);
  const shouldLoadPortalSite = shouldLookupWhiteLabelPortalByHost(hostname);
  const [customPortalDetected, setCustomPortalDetected] = useState(false);
  const [portalCheckLoading, setPortalCheckLoading] = useState(shouldLoadPortalSite && !isPortalHost);
  const isSupportHost =
    typeof window !== 'undefined' &&
    window.location.hostname.toLowerCase().startsWith('support.');
  const isStatusHost =
    typeof window !== 'undefined' &&
    window.location.hostname.toLowerCase().startsWith('status.');
  const isStoreHost =
    typeof window !== 'undefined' &&
    window.location.hostname.toLowerCase().startsWith('store.');

  useEffect(() => {
    if (!shouldLoadPortalSite || isPortalHost || !hostname) {
      setPortalCheckLoading(false);
      setCustomPortalDetected(false);
      return undefined;
    }

    let active = true;
    setPortalCheckLoading(true);
    publicAPI.getLabelSiteByHost(hostname)
      .then(() => {
        if (active) setCustomPortalDetected(true);
      })
      .catch(() => {
        if (active) setCustomPortalDetected(false);
      })
      .finally(() => {
        if (active) setPortalCheckLoading(false);
      });

    return () => {
      active = false;
    };
  }, [hostname, isPortalHost, shouldLoadPortalSite]);

  if (isPortalHost || customPortalDetected) return <Navigate to="/dashboard" replace />;
  if (portalCheckLoading) return <RouteLoadingFallback />;
  if (isSupportHost) return <Navigate to="/support" replace />;
  if (isStatusHost) return <Navigate to="/status" replace />;
  if (isStoreHost) return <Navigate to="/store" replace />;
  if (authLoading) return <RouteLoadingFallback />;
  if (user && user !== false) return <Navigate to="/account" replace />;
  return <Home />;
}

function StoreRoute() {
  const isStoreHost =
    typeof window !== 'undefined' &&
    window.location.hostname.toLowerCase().startsWith('store.');

  if (isStoreHost) {
    return <PublicStore />;
  }

  return <Stores />;
}

function RouteFallback() {
  const hostname = getCurrentHostname();
  return <Navigate to={hostname.startsWith('admin.') ? '/admin/staff-menu' : '/'} replace />;
}

function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <RouteLoadingFallback />;
}

function HostGate({ children }) {
  const location = useLocation();
  const { user, loading } = useAuth();
  const hostname = getCurrentHostname();
  const isAdminHost =
    hostname.startsWith('admin.');
  const isPortalHost = isPortalSubdomain(hostname);
  const shouldLoadPortalSite = shouldLookupWhiteLabelPortalByHost(hostname);
  const isStoreHost =
    hostname.startsWith('store.');

  if (isAdminHost) {
    const allowed =
      location.pathname === '/login' ||
      location.pathname === '/forgot-password' ||
      location.pathname === '/reset-password' ||
      location.pathname === '/change-password' ||
      location.pathname === '/upload' ||
      location.pathname === '/ourdesignsadmin' ||
      location.pathname === '/admin' ||
      location.pathname.startsWith('/admin/');

    if (!allowed) {
      return <Navigate to="/login" replace />;
    }
  }

  if (isStoreHost) {
    const path = location.pathname || '/';
    const publicAuthExactPaths = new Set([
      '/login',
      '/signup',
      '/signup/verify',
      '/forgot-password',
      '/reset-password',
      '/change-password',
      '/artist-profile',
      '/payment/success',
    ]);
    if (
      publicAuthExactPaths.has(path)
      || path.startsWith('/onboarding/')
      || path.startsWith('/payment/')
    ) {
      return <ExternalRedirect to={buildPublicSiteUrl(`${path}${location.search || ''}`)} />;
    }

    const allowed =
      location.pathname === '/dummy-store' ||
      location.pathname === '/store' ||
      location.pathname === '/stores' ||
      location.pathname.startsWith('/artist/') ||
      location.pathname.startsWith('/album/') ||
      location.pathname === '/';

    if (!allowed) {
      return <Navigate to="/store" replace />;
    }
  }

  if ((isPortalHost || shouldLoadPortalSite) && !loading && isWhiteLabelArtistPortalUser(user)) {
    const allowedExact = new Set([
      '/',
      '/overview',
      '/dashboard',
      '/login',
      '/signup',
      '/signup/verify',
      '/forgot-password',
      '/reset-password',
      '/change-password',
      '/enterprise/access-denied',
      '/enterprise/upload',
      '/enterprise/upload/bulk',
      '/upload',
      '/upload/bulk',
      '/music',
      '/videos',
      '/content-id',
      '/bank',
      '/billing',
      '/social',
      '/artist-profile',
      '/fans',
      '/awards',
      '/tools',
      '/tools/acrcloud-scanner',
      '/account',
      '/account-standing',
      '/artist-site',
      '/support',
      '/support/new',
    ]);
    const allowedPrefixes = [
      '/music/',
      '/social/',
      '/support/articles/',
      '/support/tickets/',
      '/verification/',
      '/award/',
      '/awards/',
      '/payment/',
    ];
    const path = location.pathname || '/';
    const allowed = allowedExact.has(path) || allowedPrefixes.some((prefix) => path.startsWith(prefix));
    if (!allowed) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}

const APP_VERSION_STORAGE_KEY = 'therecordbase:app-version';
const SITE_UPDATE_SEEN_STORAGE_KEY = 'therecordbase:site-update-seen';

function reloadForVersion(version) {
  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set('__v', version || 'dev');
  nextUrl.searchParams.set('__reload', String(Date.now()));
  window.location.replace(nextUrl.toString());
}

function UpdateAvailableBanner({ update, onRefresh }) {
  if (!update?.id) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] border-t border-[#66a8ff]/25 bg-[#07080d]/95 px-4 py-3 text-white shadow-[0_-18px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-white">The website has been updated</p>
          <p className="text-xs text-gray-400">{update.message || 'Reload to load the newest version of The Record Base.'}</p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex h-10 items-center justify-center rounded-md bg-[#0070f3] px-4 text-sm font-medium text-white transition-colors hover:bg-[#328bf6]"
        >
          {update.button_label || 'Reload'}
        </button>
      </div>
    </div>
  );
}

function RouteLoadingFallback() {
  return (
    <div className="min-h-screen bg-[#050609] flex items-center justify-center text-white">
      <div className="h-10 w-10 rounded-full border-2 border-white/15 border-t-[#66a8ff] animate-spin" />
    </div>
  );
}

function CoreApp() {
  const [maintenanceState, setMaintenanceState] = useState({ checked: false, enabled: false, message: '' });
  const [availableUpdate, setAvailableUpdate] = useState(null);
  const isB2BHost =
    typeof window !== 'undefined' &&
    window.location.hostname.toLowerCase().startsWith('b2b.');
  const isDistroGridHost =
    typeof window !== 'undefined' &&
    window.location.hostname.toLowerCase().startsWith('distrogrid.');
  const distroGridLookupHost = getCurrentHostname();
  const shouldCheckDistroGridTenant = !isDistroGridHost && shouldLookupWhiteLabelPortalByHost(distroGridLookupHost);
  const [distroGridTenantPortal, setDistroGridTenantPortal] = useState(null);
  const [distroGridTenantCheckLoading, setDistroGridTenantCheckLoading] = useState(shouldCheckDistroGridTenant);
  const distroGridPathMatch = typeof window !== 'undefined'
    ? window.location.pathname.match(/^\/distrogrid\/([a-z0-9]+(?:-[a-z0-9]+)*)(?=\/|$)/i)
      || (isDistroGridHost ? window.location.pathname.match(/^\/(?!pricing(?:\/|$)|developers(?:\/|$)|docs(?:\/|$)|forgot-password(?:\/|$)|reset-password(?:\/|$)|apply(?:\/|$)|signin(?:\/|$)|signup(?:\/|$)|login(?:\/|$)|admin(?:\/|$)|workspace(?:\/|$)|api(?:\/|$)|demo(?:\/|$)|preview(?:\/|$))([a-z0-9]+(?:-[a-z0-9]+)*)(?=\/|$)/i) : null)
    : null;
  const distroGridPathSlug = distroGridPathMatch?.[1]?.toLowerCase() || '';
  const [distroGridPathPortal, setDistroGridPathPortal] = useState(null);
  const [distroGridPathLoading, setDistroGridPathLoading] = useState(Boolean(distroGridPathSlug));
  const isAlarmPath =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/alarm' || window.location.pathname.startsWith('/alarm/'));

  useEffect(() => {
    if (!shouldCheckDistroGridTenant || !distroGridLookupHost) {
      setDistroGridTenantPortal(null);
      setDistroGridTenantCheckLoading(false);
      return undefined;
    }
    let active = true;
    setDistroGridTenantCheckLoading(true);
    publicAPI.getDistroGridPortalByHost(distroGridLookupHost)
      .then(({ data }) => { if (active) setDistroGridTenantPortal(data || null); })
      .catch(() => { if (active) setDistroGridTenantPortal(null); })
      .finally(() => { if (active) setDistroGridTenantCheckLoading(false); });
    return () => { active = false; };
  }, [distroGridLookupHost, shouldCheckDistroGridTenant]);

  useEffect(() => {
    if (!distroGridPathSlug) {
      setDistroGridPathPortal(null);
      setDistroGridPathLoading(false);
      return undefined;
    }
    let active = true;
    setDistroGridPathLoading(true);
    publicAPI.getDistroGridPortalBySlug(distroGridPathSlug)
      .then(({ data }) => { if (active) setDistroGridPathPortal(data || null); })
      .catch(() => { if (active) setDistroGridPathPortal(null); })
      .finally(() => { if (active) setDistroGridPathLoading(false); });
    return () => { active = false; };
  }, [distroGridPathSlug]);

  useEffect(() => {
    let active = true;
    const loadSiteStatus = async () => {
      try {
        const { data } = await publicAPI.getSiteStatus();
        if (!active) return;
        const version = String(data?.version || 'dev').trim();
        const lastVersion = window.localStorage.getItem(APP_VERSION_STORAGE_KEY) || '';
        if (version) {
          if (lastVersion && lastVersion !== version) {
            window.localStorage.setItem(APP_VERSION_STORAGE_KEY, version);
            setAvailableUpdate({
              id: `version:${version}`,
              message: 'Reload to load the newest version of The Record Base.',
              button_label: 'Reload',
            });
          }
          if (!lastVersion) {
            window.localStorage.setItem(APP_VERSION_STORAGE_KEY, version);
          }
        }
        const siteUpdateNotice = data?.site_update_notice || null;
        const siteUpdateId = String(siteUpdateNotice?.id || '').trim();
        const seenSiteUpdateId = window.localStorage.getItem(SITE_UPDATE_SEEN_STORAGE_KEY) || '';
        if (siteUpdateNotice?.enabled && siteUpdateId && seenSiteUpdateId !== siteUpdateId) {
          setAvailableUpdate(siteUpdateNotice);
        } else if (!siteUpdateNotice?.enabled && availableUpdate?.id && !String(availableUpdate.id).startsWith('version:')) {
          setAvailableUpdate(null);
        }
        setMaintenanceState({
          checked: true,
          enabled: Boolean(data?.maintenance_mode),
          message: data?.message || '',
        });
      } catch {
        if (!active) return;
        setMaintenanceState({ checked: true, enabled: false, message: '' });
      }
    };
    loadSiteStatus();
    const refreshVisibleSiteStatus = () => {
      if (document.visibilityState === 'visible') loadSiteStatus();
    };
    const timer = window.setInterval(refreshVisibleSiteStatus, 5 * 60 * 1000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadSiteStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [availableUpdate?.id]);

  const handleRefreshForUpdate = () => {
    if (availableUpdate?.id && !String(availableUpdate.id).startsWith('version:')) {
      window.localStorage.setItem(SITE_UPDATE_SEEN_STORAGE_KEY, availableUpdate.id);
    }
    reloadForVersion(availableUpdate?.id || 'dev');
  };

  if (maintenanceState.checked && maintenanceState.enabled) {
    return <Maintenance message={maintenanceState.message} />;
  }

  if (distroGridTenantCheckLoading) {
    return <RouteLoadingFallback />;
  }

  if (distroGridPathSlug) {
    if (distroGridPathLoading) return <RouteLoadingFallback />;
    if (!distroGridPathPortal) return <div className="min-h-screen bg-white flex items-center justify-center p-6 text-center"><div><h1 className="text-2xl font-semibold text-slate-900">Portal not found</h1><p className="mt-2 text-slate-500">Check the portal URL or contact The Record Base.</p></div></div>;
    const portalBasePath = window.location.pathname.toLowerCase().startsWith(`/distrogrid/${distroGridPathSlug}`) ? `/distrogrid/${distroGridPathSlug}` : `/${distroGridPathSlug}`;
    return <AppErrorBoundary><BrowserRouter basename={portalBasePath}><Suspense fallback={<RouteLoadingFallback />}><DistroGridApp initialPortalContext={distroGridPathPortal} portalSlug={distroGridPathSlug} /></Suspense></BrowserRouter></AppErrorBoundary>;
  }

  if (isDistroGridHost || distroGridTenantPortal) {
    return (
      <AppErrorBoundary>
        <BrowserRouter>
          <Suspense fallback={<RouteLoadingFallback />}>
            <DistroGridApp initialPortalContext={distroGridTenantPortal} />
          </Suspense>
        </BrowserRouter>
      </AppErrorBoundary>
    );
  }

  if (isB2BHost) {
    if (typeof window !== 'undefined' && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
      window.location.replace(`https://distrogrid.therecordbase.com${window.location.pathname}${window.location.search}`);
      return <RouteLoadingFallback />;
    }
    return <AppErrorBoundary><BrowserRouter><Suspense fallback={<RouteLoadingFallback />}><B2BApp /></Suspense></BrowserRouter></AppErrorBoundary>;
  }

  if (isAlarmPath) {
    return (
      <AuthProvider>
        <AppErrorBoundary>
          <BrowserRouter>
            <ScrollToTop />
            <RouteSeoPolicy />
            <Suspense fallback={<RouteLoadingFallback />}>
              <SafeBasePanel />
            </Suspense>
          </BrowserRouter>
        </AppErrorBoundary>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <AppErrorBoundary>
        <BrowserRouter>
          <ScrollToTop />
          <RouteSeoPolicy />
          <HostGate>
            <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomeRoute />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/trb-select" element={<TRBSelect />} />
          <Route path="/trb-music-group" element={<TRBMusicGroup />} />
          <Route path="/trb-music-group/how-it-works" element={<TRBMusicGroupHowItWorks />} />
          <Route path="/trb-music-group/submit" element={<TRBMusicGroupSubmission />} />
          <Route path="/trb-music-group/dashboard" element={
            <ProtectedRoute>
              <TRBMusicGroupDashboard />
            </ProtectedRoute>
          } />
          <Route path="/select" element={<Navigate to="/trb-select" replace />} />
          <Route path="/giveaway" element={<Giveaway />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/music-note-badge" element={<MusicNoteBadge />} />
          <Route path="/badge-eligibility" element={<MusicNoteBadge />} />
          <Route path="/metadata-health-check" element={<MusicNoteBadge />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/signup/verify" element={<SignupVerification />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/account-ban-status" element={<AccountBanStatus />} />
          <Route path="/accountbanstatus" element={<AccountBanStatus />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/invite/:inviteId" element={<InvitePage />} />
          <Route path="/accept-invite" element={<InvitePage />} />
          <Route path="/takedown" element={<TakedownPage />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/carrers" element={<Navigate to="/careers" replace />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:slug" element={<NewsArticle />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/product-suite" element={<ProductSuite />} />
          <Route path="/roadmap" element={<Roadmap />} />
          <Route path="/stores" element={<StoreRoute />} />
          <Route path="/store" element={<PublicStore />} />
          <Route path="/api-docs" element={<ApiDocs />} />
          <Route path="/developer" element={<Developer />} />
          <Route path="/developers" element={<Developer />} />
          <Route path="/mfeb" element={<Mfeb />} />
          <Route path="/mafa" element={<Navigate to="/mfeb" replace />} />
          <Route path="/cmg" element={<CMGSite />} />
          <Route path="/ourdesigns" element={<OurDesigns />} />
          <Route path="/docs" element={<Navigate to="/api-docs" replace />} />
          <Route path="/dummy-store" element={
            <AdminRoute requiredRoles={adminPanelRoles.dummyStore}>
              <DummyStoreDashboard />
            </AdminRoute>
          } />
          <Route path="/delivery" element={<Navigate to="/stores" replace />} />
          <Route path="/dsp-delivery" element={<Navigate to="/stores" replace />} />
          <Route path="/demo" element={<PublicDemo />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/book-demo" element={<BookDemo />} />
          <Route path="/book" element={<BookCalendar />} />
          <Route path="/how-distribution-works" element={<HowDistributionWorks />} />
          <Route path="/contentid" element={<ContentIDInfo />} />
          <Route path="/content-id-info" element={<Navigate to="/contentid" replace />} />
          <Route path="/why-switch" element={<WhySwitch />} />
          <Route path="/distrokid-alternative" element={<DistroKidAlternative />} />
          <Route path="/switch-from-distrokid" element={<Navigate to="/distrokid-alternative" replace />} />
          <Route path="/tunecore-alternative" element={<TuneCoreAlternative />} />
          <Route path="/music-distribution-for-labels" element={<MusicDistributionForLabels />} />
          <Route path="/product" element={<Navigate to="/product-suite" replace />} />
          <Route path="/features" element={<Navigate to="/product-suite" replace />} />
          <Route path="/features/release-command-center" element={<ReleaseCommandCenterFeature />} />
          <Route path="/features/smart-links" element={<SmartLinksFeature />} />
          <Route path="/features/team-workflow" element={<TeamWorkflowFeature />} />
          <Route path="/features/fan-crm" element={<FanCrmFeature />} />
          <Route path="/features/label-branding" element={<LabelBrandingFeature />} />
          <Route path="/features/bulk-upload" element={<BulkUploadFeature />} />
          <Route path="/features/support-status" element={<SupportStatusFeature />} />
          <Route path="/solutions/artist" element={<Navigate to="/artists" replace />} />
          <Route path="/artists" element={<ArtistSolutions />} />
          <Route path="/solutions/label" element={<Navigate to="/labels" replace />} />
          <Route path="/labels" element={<LabelSolutions />} />
          <Route path="/solutions/manager" element={<Navigate to="/managers" replace />} />
          <Route path="/managers" element={<ManagerSolutions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
          <Route path="/dmca" element={<DMCA />} />
          <Route path="/dmca-notice" element={<DmcaNotice />} />
          <Route path="/trust" element={<TrustSafety />} />
          <Route path="/copyright-policy" element={<CopyrightPolicy />} />
          <Route path="/community-guidelines" element={<CommunityGuidelines />} />
          <Route path="/verification-policy" element={<VerificationPolicy />} />
          <Route path="/sponsored-content-policy" element={<SponsoredContentPolicy />} />
          <Route path="/s/:releaseId" element={<PreSavePage />} />
          <Route path="/pre-save/c/:campaignSlug" element={<PreSavePage />} />
          <Route path="/pre-save/:releaseId" element={<PreSavePage />} />
          <Route path="/@:slug" element={<PublicBioLink />} />
          <Route path="/social" element={<SocialAccessBoundary><SocialFeed /></SocialAccessBoundary>} />
          <Route path="/social/signup" element={<SocialSignup />} />
          <Route path="/feed" element={<Navigate to="/social" replace />} />
          <Route path="/social/verification" element={
            <ProtectedRoute>
              <SocialAccessBoundary><SocialVerification /></SocialAccessBoundary>
            </ProtectedRoute>
          } />
          <Route path="/social/posts/:postId" element={<SocialAccessBoundary><SocialPost /></SocialAccessBoundary>} />
          <Route path="/social/users/:userId" element={<SocialAccessBoundary><SocialUserProfile /></SocialAccessBoundary>} />
          <Route path="/artist/:artistRef" element={<PublicArtistProfilePage />} />
          <Route path="/album/:releaseId" element={<PublicAlbumPage />} />
          <Route path="/release/:releaseId" element={<PublicAlbumPage />} />
          <Route path="/award/:awardId" element={<AwardDisplay />} />
          <Route path="/awards/wall" element={<AwardWall />} />
          <Route path="/award-wall" element={<Navigate to="/awards/wall" replace />} />
          <Route path="/alarm/*" element={<SafeBasePanel />} />
          <Route path="/:affiliateSlug" element={<PublicSlugLanding />} />

          {/* Onboarding Routes */}
          <Route path="/onboarding/plan" element={
            <ProtectedRoute>
              <OnboardingPlan />
            </ProtectedRoute>
          } />
          <Route path="/onboarding/enterprise" element={
            <ProtectedRoute>
              <OnboardingEnterprise />
            </ProtectedRoute>
          } />
          <Route path="/onboarding/profile" element={
            <ProtectedRoute>
              <OnboardingProfile />
            </ProtectedRoute>
          } />

          {/* Portal Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/artist-profile" element={
            <ProtectedRoute>
              <ArtistProfileSettings />
            </ProtectedRoute>
          } />
          <Route path="/api-portal" element={
            <ProtectedRoute>
              <ApiPortal />
            </ProtectedRoute>
          } />
          <Route path="/upload" element={
            <ProtectedRoute>
              <Upload />
            </ProtectedRoute>
          } />
          <Route path="/catalog-migration" element={
            <ProtectedRoute>
              <CatalogMigration />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/upload" element={
            <Navigate to="/upload" replace />
          } />
          <Route path="/music" element={
            <ProtectedRoute>
              <Music />
            </ProtectedRoute>
          } />
          <Route path="/mastering" element={
            <ProtectedRoute requiredAccess="ai_mastering">
              <Mastering />
            </ProtectedRoute>
          } />
          <Route path="/instant-claim" element={
            <ProtectedRoute>
              <InstantClaim />
            </ProtectedRoute>
          } />
          <Route path="/podcasts" element={<Navigate to="/music" replace />} />
          <Route path="/videos" element={
            <ProtectedRoute>
              <TRBVideoComingSoon />
            </ProtectedRoute>
          } />
          <Route path="/overview" element={
            <Navigate to="/dashboard" replace />
          } />
          <Route path="/catalog" element={
            <Navigate to="/music" replace />
          } />
          <Route path="/enterprise/qc" element={
            <ProtectedRoute requiredAccess="enterprise_portal">
              <EnterpriseDashboard section="qc" />
            </ProtectedRoute>
          } />
          <Route path="/video-network" element={
            <Navigate to="/videos" replace />
          } />
          <Route path="/vevo" element={<Navigate to="/video-network" replace />} />
          <Route path="/finance" element={
            <Navigate to="/bank" replace />
          } />
          <Route path="/oac-request" element={
            <Navigate to="/artist-profile" replace />
          } />
          <Route path="/videos/channel/create" element={
            <ProtectedRoute>
              <TRBVideoComingSoon />
            </ProtectedRoute>
          } />
          <Route path="/vevo/create" element={<Navigate to="/videos/channel/create" replace />} />
          <Route path="/bank" element={
            <ProtectedRoute>
              <Bank />
            </ProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          } />
          <Route path="/account" element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          } />
          <Route path="/account-standing" element={
            <ProtectedRoute>
              <AccountStanding />
            </ProtectedRoute>
          } />
          <Route path="/security" element={
            <ProtectedRoute>
              <SecurityCenter />
            </ProtectedRoute>
          } />
          <Route path="/privacy-center" element={
            <ProtectedRoute>
              <PrivacyCenter />
            </ProtectedRoute>
          } />
          <Route path="/awards" element={
            <ProtectedRoute>
              <Awards />
            </ProtectedRoute>
          } />
          <Route path="/tools" element={<Navigate to="/music" replace />} />
          <Route path="/tools/acrcloud-scanner" element={
            <ProtectedRoute>
              <AcrCloudScanner />
            </ProtectedRoute>
          } />
          <Route path="/content-id" element={
            <ProtectedRoute>
              <ContentIDDashboard />
            </ProtectedRoute>
          } />
          <Route path="/fans" element={
            <ProtectedRoute>
              <Fans />
            </ProtectedRoute>
          } />
          <Route path="/team" element={
            <ProtectedRoute requiredAccess="team_management">
              <LabelTeam />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/dashboard" element={
            <Navigate to="/dashboard" replace />
          } />
          <Route path="/enterprise/access-denied" element={<EnterpriseAccessDenied />} />
          <Route path="/enterprise/dashboard/team" element={
            <Navigate to="/team" replace />
          } />
          <Route path="/website" element={
            <ProtectedRoute requiredAccess="team_management">
              <LabelWebsite />
            </ProtectedRoute>
          } />
          <Route path="/artist-site" element={
            <ProtectedRoute>
              <ArtistWebsite />
            </ProtectedRoute>
          } />
          <Route path="/pre-saves" element={<ProtectedRoute><PreSaveCampaigns /></ProtectedRoute>} />
          <Route path="/bio-links" element={
            <ProtectedRoute>
              <BioLinks />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/dashboard/catalog" element={
            <Navigate to="/catalog" replace />
          } />
          <Route path="/enterprise/dashboard/qc" element={
            <Navigate to="/enterprise/qc" replace />
          } />
          <Route path="/enterprise/dashboard/vevo" element={
            <Navigate to="/video-network" replace />
          } />
          <Route path="/enterprise/dashboard/finance" element={
            <Navigate to="/finance" replace />
          } />
          <Route path="/enterprise/artists" element={
            <ProtectedRoute requiredAccess="artist_roster">
              <EnterpriseArtists />
            </ProtectedRoute>
          } />
          <Route path="/support" element={
            <Support />
          } />
          <Route path="/support/new" element={
            <ProtectedRoute>
              <SupportNewTicket />
            </ProtectedRoute>
          } />
          <Route path="/support/articles/:slug" element={<SupportArticle />} />
          <Route path="/support/tickets/:ticketId" element={
            <ProtectedRoute>
              <SupportTicket />
            </ProtectedRoute>
          } />
          <Route path="/status" element={<Status />} />
          <Route path="/verification/:releaseId" element={
            <ProtectedRoute>
              <VerificationPortal />
            </ProtectedRoute>
          } />
          <Route path="/bulk-upload" element={
            <ProtectedRoute requiredAccess="bulk_upload">
              <BulkUpload />
            </ProtectedRoute>
          } />
          <Route path="/upload/bulk" element={
            <ProtectedRoute requiredAccess="bulk_upload">
              <BulkUpload />
            </ProtectedRoute>
          } />
          <Route path="/enterprise/upload/bulk" element={
            <Navigate to="/upload/bulk" replace />
          } />
          <Route path="/music/:releaseId/command-center" element={
            <ProtectedRoute>
              <ReleaseCommandCenter />
            </ProtectedRoute>
          } />
          <Route path="/cmgadmin" element={
            <CMGAdmin />
          } />
          <Route path="/cmgadmin/upload" element={
            <ProtectedRoute>
              <Upload cmgMode />
            </ProtectedRoute>
          } />
          <Route path="/ourdesignsadmin" element={
            <AdminRoute requiredRoles={adminPanelRoles.ourDesigns}>
              <AdminOurDesigns />
            </AdminRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin" element={
            <Navigate to="/admin/staff-menu" replace />
          } />
          <Route path="/admin/" element={
            <Navigate to="/admin/staff-menu" replace />
          } />
          <Route path="/admin/staff-menu" element={
            <AdminRoute>
              <AdminStaffMenu />
            </AdminRoute>
          } />
          <Route path="/admin/staff-helper" element={
            <AdminRoute requiredRoles={adminPanelRoles.staffHelper}>
              <AdminStaffHelper />
            </AdminRoute>
          } />
          <Route path="/admin/staff-help" element={
            <AdminRoute requiredRoles={adminPanelRoles.staffHelp}>
              <AdminStaffHelp />
            </AdminRoute>
          } />
          <Route path="/admin/queue" element={
            <AdminRoute requiredRoles={adminPanelRoles.queue}>
              <AdminQueue />
            </AdminRoute>
          } />
          <Route path="/admin/risk-queue" element={
            <AdminRoute requiredRoles={adminPanelRoles.queue}>
              <AdminRiskQueue />
            </AdminRoute>
          } />
          <Route path="/admin/releases" element={
            <AdminRoute requiredRoles={adminPanelRoles.releaseManager}>
              <AdminReleases />
            </AdminRoute>
          } />
          <Route path="/admin/store" element={
            <AdminRoute requiredRoles={adminPanelRoles.releaseManager}>
              <TRBStoreDashboard />
            </AdminRoute>
          } />
          <Route path="/admin/bank" element={
            <AdminRoute requiredRoles={adminPanelRoles.bank}>
              <AdminBank />
            </AdminRoute>
          } />
          <Route path="/admin/billing" element={
            <AdminRoute requiredRoles={adminPanelRoles.billing}>
              <AdminBilling />
            </AdminRoute>
          } />
          <Route path="/admin/podcasts" element={<Navigate to="/admin/queue" replace />} />
          <Route path="/admin/takedowns" element={
            <AdminRoute requiredRoles={adminPanelRoles.takedowns}>
              <AdminTakedowns />
            </AdminRoute>
          } />
          <Route path="/admin/partners" element={
            <AdminRoute requiredRoles={adminPanelRoles.partners}>
              <AdminPartners />
            </AdminRoute>
          } />
          <Route path="/admin/developer" element={
            <AdminRoute requiredRoles={adminPanelRoles.developer}>
              <AdminDeveloper />
            </AdminRoute>
          } />
          <Route path="/admin/tickets" element={
            <AdminRoute requiredRoles={adminPanelRoles.tickets}>
              <AdminTickets />
            </AdminRoute>
          } />
          <Route path="/admin/receiving" element={
            <AdminRoute requiredRoles={adminPanelRoles.mailbox}>
              <AdminReceiving />
            </AdminRoute>
          } />
          <Route path="/admin/receiving/settings" element={
            <AdminRoute requiredRoles={adminPanelRoles.mailboxAdmin}>
              <AdminMailboxSettings />
            </AdminRoute>
          } />
          <Route path="/admin/help-articles" element={
            <AdminRoute requiredRoles={adminPanelRoles.helpArticles}>
              <AdminHelpArticles />
            </AdminRoute>
          } />
          <Route path="/admin/export" element={
            <AdminRoute requiredRoles={adminPanelRoles.export}>
              <AdminExport />
            </AdminRoute>
          } />
          <Route path="/admin/sandbox-upload" element={<Navigate to="/admin/export" replace />} />
          <Route path="/admin/sandbox-export" element={<Navigate to="/admin/export" replace />} />
          <Route path="/admin/sandbox-test-queue" element={<Navigate to="/admin/export" replace />} />
          <Route path="/admin/ddex" element={
            <AdminRoute requiredRoles={adminPanelRoles.ddex}>
              <AdminDDEX />
            </AdminRoute>
          } />
          <Route path="/admin/ddex-test-creator" element={
            <AdminRoute requiredRoles={adminPanelRoles.ddex}>
              <AdminDDEXTestCreator />
            </AdminRoute>
          } />
          <Route path="/admin/special-access" element={
            <AdminRoute requiredRoles={adminPanelRoles.specialAccess}>
              <AdminSpecialAccess />
            </AdminRoute>
          } />
          <Route path="/admin/staff" element={
            <AdminRoute requiredRoles={adminPanelRoles.staff}>
              <AdminStaff />
            </AdminRoute>
          } />
          <Route path="/admin/staff-chat" element={
            <AdminRoute requiredRoles={adminPanelRoles.staff}>
              <RocketChatStaff />
            </AdminRoute>
          } />
          <Route path="/admin/site-staff" element={
            <AdminRoute requiredRoles={adminPanelRoles.siteStaff}>
              <AdminSiteStaff />
            </AdminRoute>
          } />
          <Route path="/admin/account-managers" element={
            <AdminRoute requiredRoles={adminPanelRoles.accountManagers}>
              <AdminAccountManagers />
            </AdminRoute>
          } />
          <Route path="/admin/careers" element={
            <AdminRoute requiredRoles={adminPanelRoles.careers}>
              <AdminCareers />
            </AdminRoute>
          } />
          <Route path="/admin/moderation" element={<AdminRoute requiredRoles={adminPanelRoles.users}><AdminModeration /></AdminRoute>} />
          <Route path="/admin/distrogrid" element={<AdminRoute requiredRoles={adminPanelRoles.distroGrid}><AdminDistroGrid /></AdminRoute>} />
          <Route path="/admin/users" element={
            <AdminRoute requiredRoles={adminPanelRoles.users}>
              <AdminUsers />
            </AdminRoute>
          } />
          <Route path="/admin/marketing" element={
            <AdminRoute requiredRoles={adminPanelRoles.marketing}>
              <AdminMarketing />
            </AdminRoute>
          } />
          <Route path="/admin/calendar" element={
            <AdminRoute requiredRoles={adminPanelRoles.calendar}>
              <AdminCalendar />
            </AdminRoute>
          } />
          <Route path="/admin/ourdesignsadmin" element={
            <AdminRoute requiredRoles={adminPanelRoles.ourDesigns}>
              <AdminOurDesigns />
            </AdminRoute>
          } />
          <Route path="/admin/mfeb" element={
            <AdminRoute requiredRoles={adminPanelRoles.mfeb}>
              <AdminMfeb />
            </AdminRoute>
          } />
          <Route path="/admin/mafa" element={<Navigate to="/admin/mfeb" replace />} />
          <Route path="/admin/artist-awards" element={
            <AdminRoute requiredRoles={adminPanelRoles.artistAwards}>
              <AdminArtistAwards />
            </AdminRoute>
          } />
          <Route path="/admin/award-wall" element={
            <AdminRoute requiredRoles={adminPanelRoles.artistAwards}>
              <AdminAwardWall />
            </AdminRoute>
          } />
          <Route path="/admin/affiliates" element={
            <AdminRoute requiredRoles={adminPanelRoles.affiliates}>
              <AdminAffiliates />
            </AdminRoute>
          } />
          <Route path="/admin/news" element={
            <AdminRoute requiredRoles={adminPanelRoles.news}>
              <AdminNews />
            </AdminRoute>
          } />
          <Route path="/admin/social" element={
            <AdminRoute requiredRoles={adminPanelRoles.social}>
              <AdminSocial />
            </AdminRoute>
          } />
          <Route path="/admin/added-updates" element={
            <AdminRoute requiredRoles={adminPanelRoles.news}>
              <AdminAddedUpdates />
            </AdminRoute>
          } />
          <Route path="/admin/status" element={
            <AdminRoute requiredRoles={adminPanelRoles.news}>
              <AdminStatusComponents />
            </AdminRoute>
          } />
          <Route path="/admin/roadmap" element={
            <AdminRoute requiredRoles={adminPanelRoles.roadmap}>
              <AdminRoadmap />
            </AdminRoute>
          } />
          <Route path="/admin/legal" element={
            <AdminRoute requiredRoles={adminPanelRoles.legal}>
              <AdminLegal />
            </AdminRoute>
          } />
          <Route path="/admin/company-policies" element={
            <AdminRoute requiredRoles={adminPanelRoles.home}>
              <AdminCompanyPolicies />
            </AdminRoute>
          } />
          <Route path="/admin/boardroom" element={
            <AdminRoute requiredRoles={adminPanelRoles.boardroom}>
              <AdminBoardroom />
            </AdminRoute>
          } />
          <Route path="/admin/boardroom/documents" element={
            <AdminRoute requiredRoles={adminPanelRoles.boardroom}>
              <AdminBoardroomDocuments />
            </AdminRoute>
          } />
          <Route path="/admin/ffm" element={<AdminRoute requiredRoles={adminPanelRoles.presaves}><AdminFFM /></AdminRoute>} />
          <Route path="/admin/presaves" element={
            <AdminRoute requiredRoles={adminPanelRoles.presaves}>
              <AdminPresaves />
            </AdminRoute>
          } />
          <Route path="/admin/compliance" element={
            <AdminRoute requiredRoles={adminPanelRoles.compliance}>
              <AdminCompliance />
            </AdminRoute>
          } />
          <Route path="/admin/enterprise-access" element={
            <AdminRoute requiredRoles={adminPanelRoles.enterpriseAccess}>
              <AdminEnterpriseAccess />
            </AdminRoute>
          } />
          <Route path="/admin/enterprise-access-portals" element={
            <AdminRoute requiredRoles={adminPanelRoles.enterpriseAccess}>
              <AdminEnterpriseAccess />
            </AdminRoute>
          } />
          <Route path="/admin/owner-vault" element={
            <AdminRoute requiredRoles={adminPanelRoles.ownerVault}>
              <AdminOwnerVault />
            </AdminRoute>
          } />
          <Route path="/admin/docs" element={<Navigate to="/admin/boardroom/documents" replace />} />
          <Route path="/admin/lyrics" element={
            <AdminRoute requiredRoles={adminPanelRoles.lyrics}>
              <AdminLyrics />
            </AdminRoute>
          } />
          <Route path="/admin/content-id" element={
            <AdminRoute requiredRoles={adminPanelRoles.contentId}>
              <AdminContentID />
            </AdminRoute>
          } />
          <Route path="/admin/needs-attention" element={
            <AdminRoute requiredRoles={adminPanelRoles.needsAttention}>
              <AdminNeedsAttention />
            </AdminRoute>
          } />
          <Route path="/admin/acrcloud-scanner" element={
            <AdminRoute requiredRoles={adminPanelRoles.acrCloud}>
              <AdminAcrCloudScanner />
            </AdminRoute>
          } />
          <Route path="/admin/demo-accounts" element={
            <AdminRoute requiredRoles={adminPanelRoles.demoAccounts}>
              <AdminDemoAccounts />
            </AdminRoute>
          } />

          {/* Fallback */}
          <Route path="*" element={<RouteFallback />} />
          </Routes>
            </Suspense>
          </HostGate>
          <AgreementModal />
        </BrowserRouter>
        <UpdateAvailableBanner update={availableUpdate} onRefresh={handleRefreshForUpdate} />
        <Toaster position="top-right" />
      </AppErrorBoundary>
    </AuthProvider>
  );
}

function App() {
  const isBrowser = typeof window !== 'undefined';
  const pathname = isBrowser ? window.location.pathname : '';
  const isJaxonWestMusicHost = isBrowser && ['jaxonwestmusic.com', 'www.jaxonwestmusic.com'].includes(window.location.hostname.toLowerCase());
  const isFreelancePath = pathname === '/freelance' || pathname.startsWith('/freelance/');
  const isJaxonWestMusicAdminPath = isJaxonWestMusicHost && (pathname === '/admin' || pathname.startsWith('/admin/'));

  if (isFreelancePath || isJaxonWestMusicAdminPath) {
    return (
      <AppErrorBoundary>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              <Route path="/freelance/*" element={<Freelance />} />
              <Route path="/admin/*" element={<JaxonWestMusicAdmin />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AppErrorBoundary>
    );
  }

  return <CoreApp />;
}

export default App;
